import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomPaginationTable from '../../components/table/CustomPaginationTable';
import {
  fetchOrderQuery,
  setQueryParams,
  exportOrderQuery,
  resendOrderNotification
} from '../../redux/orderQuerySlice';
import './ViewOrder.css';
import { QRCodeSVG } from 'qrcode.react';
import ConfirmResendModal from '../../components/confirmResendModal/ConfirmResendModal';

function ViewOrder() {
  const dispatch = useDispatch();
  const { data, loading, error, pagination, exportLoading } = useSelector(state => state.orderQuery);

  const [orderId, setOrderId] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [resendingOrder, setResendingOrder] = useState(null);

  const buildQueryParams = useCallback((page = 0, size = pagination.pageSize || 10) => {
    const params = {
      page: page,
      size: size
    };

    if (orderId.trim()) {
      params.orderNo = orderId.trim();
    }

    if (customerEmail.trim()) {
      params.email = customerEmail.trim();
    }

    return params;
  }, [orderId, customerEmail, pagination.pageSize]);

  const handleSearch = useCallback(() => {
    const queryParams = buildQueryParams(0);
    dispatch(setQueryParams(queryParams));
    dispatch(fetchOrderQuery(queryParams));
  }, [dispatch, buildQueryParams]);

  const handleReset = () => {
    setOrderId('');
    setCustomerEmail('');
    dispatch(setQueryParams({}));
    dispatch(fetchOrderQuery({ page: 0, size: 10 }));
  };

  const handleExport = () => {
    const exportParams = {};

    if (orderId.trim()) {
      exportParams.orderNo = orderId.trim();
    }

    if (customerEmail.trim()) {
      exportParams.email = customerEmail.trim();
    }

    dispatch(exportOrderQuery({ queryParams: exportParams, fileNamePrefix: 'order-query' }));
  };

  const handleResendClick = (order) => {
    setSelectedOrder(order);
    setShowConfirmModal(true);
  };

  const handleConfirmResend = () => {
    if (!selectedOrder) return;

    setShowConfirmModal(false);
    setResendingOrder(selectedOrder.transactionId);

    dispatch(resendOrderNotification(selectedOrder.transactionId))
      .finally(() => {
        setResendingOrder(null);
        setSelectedOrder(null);
      });
  };

  const handleCloseConfirmModal = () => {
    setShowConfirmModal(false);
    setSelectedOrder(null);
  };

  const handlePageChange = (page) => {
    const apiPage = page - 1;
    const queryParams = buildQueryParams(apiPage);
    dispatch(fetchOrderQuery(queryParams));
  };

  const handlePerRowsChange = (newPerPage, page) => {
    const queryParams = {
      ...buildQueryParams(0, newPerPage),
      size: newPerPage
    };
    dispatch(fetchOrderQuery(queryParams));
  };

  useEffect(() => {
    if (!isInitialized) {
      dispatch(fetchOrderQuery({ page: 0, size: 10 }));
      setIsInitialized(true);
    }
  }, [dispatch, isInitialized]);

  const tableData = data ? data.map(item => ({
    transactionId: item.transactionId,
    orderNo: item.orderNo,
    orderStatus: item.orderStatus,
    subOrderStatus: item.subOrderStatus,
    purchaseDate: item.createdDatetime || 'N/A',
    email: item.orderDetail?.email || 'N/A',
    name: `${item.orderDetail?.lastName || ''} ${item.orderDetail?.firstName || ''}`.trim() || 'N/A',
    msisdn: item.orderDetail?.esim?.msisdn || 'N/A',
    iccid: item.orderDetail?.esim?.iccid || 'N/A',
    mvneOrderId: item.orderDetail?.mvneOrderId || 'N/A',
    product: item.orderDetail?.product?.name || 'N/A',
    sku: item.orderDetail?.product?.sku || 'N/A',
    promoCode: item.orderDetail?.promoCode || 'N/A',
    qrString: item.orderDetail?.qrString || 'N/A'
  })) : [];

  const columns = [
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Order Number</div>,
      selector: row => row.orderNo,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.orderNo}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Order Status</div>,
      selector: row => row.orderStatus,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.orderStatus}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Sub Order Status</div>,
      selector: row => row.subOrderStatus,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.subOrderStatus}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Purchase Date</div>,
      selector: row => row.purchaseDate,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.purchaseDate}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Email</div>,
      selector: row => row.email,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.email}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Name</div>,
      selector: row => row.name,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.name}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>MSISDN</div>,
      selector: row => row.msisdn,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.msisdn}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>ICCID</div>,
      selector: row => row.iccid,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.iccid}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>MVNE Order ID</div>,
      selector: row => row.mvneOrderId,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.mvneOrderId}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Product</div>,
      selector: row => row.product,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.product}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>SKU</div>,
      selector: row => row.sku,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.sku}
        </div>
      ),
      wrap: true,
      width: '85px'
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Promo Code</div>,
      selector: row => row.promoCode,
      cell: row => (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          {row.promoCode}
        </div>
      ),
      wrap: true
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>QR String</div>,
      selector: row => row.qrString,
      cell: row => row.qrString && row.qrString !== 'N/A' ? (
        <div style={{ textAlign: 'center' }}>
          <QRCodeSVG
            value={row.qrString}
            size={50}
            level="M"
          />
        </div>
      ) : (
        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
          N/A
        </div>
      ),
      width: '120px'
    },
    {
      name: <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>Action</div>,
      cell: row => (
        <div style={{ whiteSpace: 'nowrap' }}>
          {row.subOrderStatus === 'completed' ? (
            <button
              className="resend-button"
              onClick={() => handleResendClick(row)}
              disabled={resendingOrder === row.transactionId}
              style={{
                padding: '4px 12px',
                backgroundColor: resendingOrder === row.transactionId ? '#6c757d' : '#ED1A3D',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: resendingOrder === row.transactionId ? 'not-allowed' : 'pointer',
                fontSize: '12px',
                fontWeight: '500',
                minWidth: '80px',
                opacity: resendingOrder === row.transactionId ? 0.6 : 1
              }}
            >
              {resendingOrder === row.transactionId ? 'Sending...' : 'Resend'}
            </button>
          ) : (
            <span>N/A</span>
          )}
        </div>
      ),
      width: '100px',
      center: true
    }
  ];

  return (
    <div className='view-order-container'>
      {/* Confirm Resend Modal */}
      <ConfirmResendModal
        show={showConfirmModal}
        onHide={handleCloseConfirmModal}
        onConfirm={handleConfirmResend}
        orderNo={selectedOrder?.orderNo || ''}
        email={selectedOrder?.email || ''}
      />

      <div className="view-order-content">
        <h3>View Order</h3>

        <div className="view-order-search-section">
          <div className="view-order-search-form">
            <div className="view-order-form-group">
              <input
                type="text"
                id="orderId"
                placeholder="Order number"
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>

            <div className="view-order-form-group">
              <input
                type="text"
                id="customerEmail"
                placeholder="Email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>

            <div className="view-order-button-group">
              <button
                className="view-order-search-btn"
                onClick={handleSearch}
                disabled={loading}
              >
                {loading ? 'SEARCHING...' : 'SEARCH'}
              </button>
              <button
                className="view-order-search-btn"
                onClick={handleReset}
                disabled={loading}
              >
                RESET
              </button>
              <button
                className="view-order-search-btn"
                onClick={handleExport}
                disabled={exportLoading || loading || !data?.length}
              >
                {exportLoading ? 'EXPORTING...' : 'EXPORT'}
              </button>
            </div>
          </div>
        </div>

        <div>
          <CustomPaginationTable
            data={tableData}
            totalRows={pagination.totalRecords || 0}
            loading={loading}
            perPage={pagination.pageSize || 10}
            currentPage={(pagination.pageNumber || 0) + 1}
            onPageChange={handlePageChange}
            columns={columns}
            onPerRowsChange={handlePerRowsChange}
            isHiddenPag={false}
          />
        </div>
      </div>
    </div>
  );
}

export default ViewOrder;